<template>
    <div class="update-detail">
        <div class="section">
            <div class="section-title">{{ $t("googleDrive.channelInfo") }}</div>
            <div class="section-content">
                <div class="form-row">
                    <label>{{ $t("googleDrive.channelName") }}</label>
                    <a-input v-model="localChannel.name" :placeholder="$t('googleDrive.channelName')" />
                </div>
                <div class="form-row" v-if="localChannel.payload && localChannel.payload.email">
                    <label>{{ $t("googleDrive.connectedAccount") }}</label>
                    <div class="account-info">
                        <img 
                            v-if="localChannel.payload.photoLink" 
                            :src="localChannel.payload.photoLink" 
                            class="account-photo"
                        />
                        <div class="account-details">
                            <span class="display-name">{{ localChannel.payload.displayName }}</span>
                            <span class="email">{{ localChannel.payload.email }}</span>
                        </div>
                    </div>
                </div>
                <div class="form-row" v-if="localChannel.payload && localChannel.payload.storageQuota">
                    <label>{{ $t("googleDrive.storageQuota") }}</label>
                    <div class="storage-info">
                        <a-progress 
                            :percent="storagePercent" 
                            :status="storagePercent > 90 ? 'exception' : 'normal'"
                        />
                        <span class="storage-text">
                            {{ formatBytes(localChannel.payload.storageQuota.usage) }} / 
                            {{ formatBytes(localChannel.payload.storageQuota.limit) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-footer">
            <SupsisButton primary @click="save" :loading="saving">
                {{ $t("message.save") }}
            </SupsisButton>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

export default {
    name: "UpdateDetail",
    components: {
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            localChannel: JSON.parse(JSON.stringify(this.value)),
            saving: false,
        };
    },
    computed: {
        storagePercent() {
            const quota = this.localChannel.payload && this.localChannel.payload.storageQuota;
            if (!quota || !quota.limit || quota.limit === "0") return 0;
            return Math.round((parseInt(quota.usage) / parseInt(quota.limit)) * 100);
        },
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        formatBytes(bytes) {
            if (!bytes) return "0 B";
            const k = 1024;
            const sizes = ["B", "KB", "MB", "GB", "TB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },
        async save() {
            this.saving = true;
            try {
                await this.updateChannel({
                    id: this.localChannel._id,
                    data: {
                        name: this.localChannel.name,
                    },
                });
                this.$message.success(this.$t("auto.googleDriveChannelSaveSuccess"));
                this.$emit("saved");
            } catch (error) {
                this.$message.error(this.$t("auto.googleDriveChannelSaveError"));
            } finally {
                this.saving = false;
            }
        },
    },
    watch: {
        value: {
            handler(val) {
                this.localChannel = JSON.parse(JSON.stringify(val));
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.update-detail {
    padding: 20px;

    .section {
        background: var(--color-bg-default);
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        margin-bottom: 20px;

        .section-title {
            font-size: 16px;
            font-weight: 600;
            padding: 15px 20px;
            border-bottom: 1px solid var(--color-border-default);
        }

        .section-content {
            padding: 20px;

            .form-row {
                margin-bottom: 20px;

                &:last-child {
                    margin-bottom: 0;
                }

                label {
                    display: block;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .account-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: var(--color-bg-secondary);
                    border-radius: 8px;

                    .account-photo {
                        width: 48px;
                        height: 48px;
                        border-radius: 50%;
                    }

                    .account-details {
                        display: flex;
                        flex-direction: column;

                        .display-name {
                            font-weight: 600;
                            font-size: 14px;
                        }

                        .email {
                            color: var(--color-text-secondary);
                            font-size: 13px;
                        }
                    }
                }

                .storage-info {
                    .storage-text {
                        display: block;
                        margin-top: 8px;
                        font-size: 13px;
                        color: var(--color-text-secondary);
                    }
                }
            }
        }
    }

    .section-footer {
        display: flex;
        justify-content: flex-end;
        padding-top: 20px;
    }
}
</style>
